import React from 'react';
import { Play, Square, Plus } from 'lucide-react';
import { formatDuration, formatDateTimeLocal } from '../utils/formatters';
import { TimeEntry } from './TimeEntry';

export function TimerView({
  description,
  setDescription,
  selectedProject,
  setSelectedProject,
  projects,
  currentTimer,
  currentTime,
  entries,
  editingEntry,
  editForm,
  getProjectById,
  onStartTimer,
  onStopTimer,
  onUpdateTimer,
  onUpdateTimerStartTime,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onEditFormChange,
  onDeleteEntry,
  onShowProjectModal
}) {
  const getCurrentDuration = () => {
    if (!currentTimer) return 0;
    return currentTime - currentTimer.startTime;
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 rounded-xl p-6">
        <div className="space-y-3">
          <div className="flex gap-3">
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !currentTimer && onStartTimer()}
              placeholder="What are you working on?"
              disabled={currentTimer !== null}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
            />
            {!currentTimer ? (
              <button
                onClick={onStartTimer}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 font-medium transition-colors"
              >
                <Play size={20} />
                Start
              </button>
            ) : (
              <button
                onClick={onStopTimer}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 font-medium transition-colors"
              >
                <Square size={20} />
                Stop
              </button>
            )}
          </div>
          {!currentTimer && (
            <div className="flex items-center gap-2">
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
              <button
                onClick={onShowProjectModal}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors flex items-center gap-2"
                title="Add Project"
              >
                <Plus size={20} />
              </button>
            </div>
          )}
        </div>
      </div>

      {currentTimer && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="text-sm text-blue-600 font-medium mb-2">RUNNING</div>
                <input
                  type="text"
                  value={currentTimer.description}
                  onChange={(e) => onUpdateTimer(e.target.value)}
                  placeholder="What are you working on?"
                  className="w-full text-xl font-semibold text-gray-900 bg-white border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <select
                  value={currentTimer.projectId}
                  onChange={(e) => onUpdateTimer(currentTimer.description, e.target.value)}
                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {projects.map(proj => (
                    <option key={proj.id} value={proj.id}>{proj.name}</option>
                  ))}
                </select>
                <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                  <span>Started:</span>
                  <input
                    type="datetime-local"
                    value={formatDateTimeLocal(currentTimer.startTime)}
                    onChange={(e) => onUpdateTimerStartTime(e.target.value)}
                    max={formatDateTimeLocal(Date.now())}
                    className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>
              <div className="text-4xl font-bold text-blue-600 ml-6">
                {formatDuration(getCurrentDuration())}
              </div>
            </div>
          </div>
        </div>
      )}

      {entries.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Recent Entries</h2>
          <div className="space-y-2">
            {entries.slice(0, 5).map((entry) => (
              <TimeEntry
                key={entry.id}
                entry={entry}
                project={getProjectById(entry.projectId)}
                projects={projects}
                currentTime={currentTime}
                isEditing={editingEntry === entry.id}
                editForm={editForm}
                onEditFormChange={onEditFormChange}
                onStartEdit={onStartEdit}
                onSaveEdit={onSaveEdit}
                onCancelEdit={onCancelEdit}
                onDelete={onDeleteEntry}
                showDate={true}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
